import React from 'react';
import { Globe, Linkedin, Twitter, ArrowUpRight, Home, Shield, Sparkles, Cloud, Mail, Phone, MapPin } from 'lucide-react';

/**
 * Enhanced Footer Component - Smartsocs-inspired
 */

const Footer: React.FC = () => {
    return (
        <footer className="bg-[#0a1628] border-t border-white/10">
            {/* Main Footer */}
            <div className="max-w-7xl mx-auto px-6 py-16">
                <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-10">
                    {/* Brand Column */}
                    <div className="lg:col-span-2">
                        <div className="flex flex-col items-start mb-6">
                            <img src="/lancesoft-logo.png" alt="LanceSoft" className="h-8 brightness-0 invert mb-2" />
                            <span className="text-[10px] tracking-[0.15em] text-[#55C3BD] uppercase font-medium">VLSI</span>
                        </div>
                        <p className="text-white/50 text-sm leading-relaxed mb-6 max-w-sm">
                            Your turnkey engineering partner for VLSI, Semiconductor, and Embedded Systems solutions.
                            From concept to silicon, we deliver excellence.
                        </p>
                        <div className="flex gap-3">
                            <a href="https://linkedin.com/company/lancesoft" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-[#55C3BD] hover:border-[#55C3BD]/50 transition-all">
                                <Linkedin size={18} />
                            </a>
                            <a href="https://twitter.com/lancesoftinc" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-[#55C3BD] hover:border-[#55C3BD]/50 transition-all">
                                <Twitter size={18} />
                            </a>
                        </div>
                    </div>

                    {/* Services */}
                    <div>
                        <h4 className="text-white font-semibold mb-6 text-sm uppercase tracking-wider">Services</h4>
                        <ul className="space-y-3">
                            <li><a href="#capabilities" className="text-white/50 text-sm hover:text-[#55C3BD] transition-colors">Design Verification</a></li>
                            <li><a href="#capabilities" className="text-white/50 text-sm hover:text-[#55C3BD] transition-colors">Physical Design</a></li>
                            <li><a href="#capabilities" className="text-white/50 text-sm hover:text-[#55C3BD] transition-colors">DFT</a></li>
                            <li><a href="#capabilities" className="text-white/50 text-sm hover:text-[#55C3BD] transition-colors">Analog/Mixed-Signal</a></li>
                            <li><a href="#capabilities" className="text-white/50 text-sm hover:text-[#55C3BD] transition-colors">Post-Silicon Validation</a></li>
                            <li><a href="#capabilities" className="text-white/50 text-sm hover:text-[#55C3BD] transition-colors">Embedded Software</a></li>
                        </ul>
                    </div>

                    {/* Ecosystem */}
                    <div>
                        <h4 className="text-white font-semibold mb-6 text-sm uppercase tracking-wider">Ecosystem</h4>
                        <ul className="space-y-3">
                            <li>
                                <a href="../" className="flex items-center gap-2 text-white/50 text-sm hover:text-[#55C3BD] transition-colors">
                                    <Home size={14} /> Global Site <ArrowUpRight size={12} />
                                </a>
                            </li>
                            <li>
                                <a href="../experience-studio/" className="flex items-center gap-2 text-white/50 text-sm hover:text-[#55C3BD] transition-colors">
                                    <Sparkles size={14} /> Experience Studio <ArrowUpRight size={12} />
                                </a>
                            </li>
                            <li>
                                <a href="../microsoft-solutions/" className="flex items-center gap-2 text-white/50 text-sm hover:text-[#55C3BD] transition-colors">
                                    <Cloud size={14} /> Microsoft Solutions <ArrowUpRight size={12} />
                                </a>
                            </li>
                            <li>
                                <a href="../salesforce-solutions/" className="flex items-center gap-2 text-white/50 text-sm hover:text-[#55C3BD] transition-colors">
                                    <Cloud size={14} /> Salesforce Solutions <ArrowUpRight size={12} />
                                </a>
                            </li>
                            <li>
                                <a href="../cybersecurity/" className="flex items-center gap-2 text-white/50 text-sm hover:text-[#55C3BD] transition-colors">
                                    <Shield size={14} /> Cybersecurity <ArrowUpRight size={12} />
                                </a>
                            </li>
                        </ul>
                    </div>

                    {/* Contact */}
                    <div>
                        <h4 className="text-white font-semibold mb-6 text-sm uppercase tracking-wider">Contact</h4>
                        <ul className="space-y-4">
                            <li className="flex items-start gap-3">
                                <MapPin size={16} className="text-[#55C3BD] mt-1 shrink-0" />
                                <span className="text-white/50 text-sm">13454 Sunrise Valley Dr, Suite 120, Herndon, VA 20171</span>
                            </li>
                            <li className="flex items-center gap-3">
                                <Mail size={16} className="text-[#55C3BD] shrink-0" />
                                <a href="mailto:vlsi@lancesoft.com" className="text-[#55C3BD] text-sm hover:text-[#55C3BD]/80 transition-colors">vlsi@lancesoft.com</a>
                            </li>
                            <li className="flex items-center gap-3">
                                <Phone size={16} className="text-[#55C3BD] shrink-0" />
                                <a href="tel:+17035549060" className="text-white/50 text-sm hover:text-[#55C3BD] transition-colors">+1 (703) 554-9060</a>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Bottom Bar */}
            <div className="border-t border-white/10">
                <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row justify-between items-center gap-4">
                    <p className="text-white/40 text-sm">
                        © {new Date().getFullYear()} LanceSoft Inc. All rights reserved.
                    </p>
                    <div className="flex gap-8">
                        <a href="#" className="text-white/40 text-xs hover:text-white/60 transition-colors">Privacy Policy</a>
                        <a href="#" className="text-white/40 text-xs hover:text-white/60 transition-colors">Terms of Service</a>
                        <a href="#" className="text-white/40 text-xs hover:text-white/60 transition-colors">Cookie Policy</a>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
